/**
 * Animation primitives.
 *
 * Pure functions: given an AnimationSpec and a local time, return a transform
 * (opacity, scale, translate, blur, letterSpacing delta). Both preview and
 * export evaluate these at the same logical time, so motion matches frame for
 * frame.
 */
export const IDENTITY = {
    opacity: 1,
    scale: 1,
    translateXPx: 0,
    translateYPx: 0,
    blurPx: 0,
    letterSpacingDeltaPx: 0,
    rotateDeg: 0,
    scaleYMul: 1,
};
export function ease(t, kind) {
    const x = t < 0 ? 0 : t > 1 ? 1 : t;
    switch (kind) {
        case 'linear':
            return x;
        case 'easeOut':
            return 1 - Math.pow(1 - x, 3);
        case 'easeInOut':
            return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
        case 'spring':
            return springValue(x);
        default:
            return x;
    }
}
/**
 * Deterministic damped-spring curve: rises past 1 (overshoot) then settles to
 * exactly 1 at x=1, and is exactly 0 at x=0. No time-step simulation, so it is
 * identical in browser and node.
 */
function springValue(x) {
    if (x <= 0)
        return 0;
    if (x >= 1)
        return 1;
    const decay = Math.exp(-6 * x);
    const oscillation = Math.sin(x * Math.PI * 2.2) * decay * 0.35;
    const base = 1 - Math.pow(1 - x, 2); // smooth rise to 1
    return base + oscillation;
}
/**
 * Evaluate an entrance animation. `localMs` is time since the animation's
 * start (after its delay is applied by the caller). Returns a Transform that,
 * at localMs<=0 is the "from" state and at localMs>=duration is IDENTITY.
 */
export function evalEntrance(spec, localMs) {
    if (spec.kind === 'none' || spec.durationMs <= 0)
        return IDENTITY;
    const t = clamp01((localMs - spec.delayMs) / spec.durationMs);
    const e = ease(t, spec.easing);
    const inv = 1 - e;
    const k = spec.intensity;
    switch (spec.kind) {
        case 'fade':
            return { ...IDENTITY, opacity: e };
        case 'scale':
            return { ...IDENTITY, opacity: e, scale: 1 - inv * 0.4 * k };
        case 'pop':
            return { ...IDENTITY, opacity: Math.min(1, e * 1.5), scale: popScale(t, k) };
        case 'slideUp':
            return { ...IDENTITY, opacity: e, translateYPx: inv * 60 * k };
        case 'slideDown':
            return { ...IDENTITY, opacity: e, translateYPx: -inv * 60 * k };
        case 'blurIn':
            return { ...IDENTITY, opacity: e, blurPx: inv * 12 * k };
        case 'bounce':
            return { ...IDENTITY, opacity: e, translateYPx: bounce(t) * 40 * k };
        case 'trackingExpand':
            return { ...IDENTITY, opacity: e, letterSpacingDeltaPx: inv * 14 * k };
        default:
            return IDENTITY;
    }
}
/** Exit animation: at localMs<=0 IDENTITY, at localMs>=duration fully gone. */
export function evalExit(spec, localMs) {
    if (spec.kind === 'none' || spec.durationMs <= 0)
        return IDENTITY;
    const t = clamp01((localMs - spec.delayMs) / spec.durationMs);
    const e = ease(t, spec.easing);
    const k = spec.intensity;
    switch (spec.kind) {
        case 'fade':
            return { ...IDENTITY, opacity: 1 - e };
        case 'scale':
            return { ...IDENTITY, opacity: 1 - e, scale: 1 - e * 0.25 * k };
        case 'slideUp':
            return { ...IDENTITY, opacity: 1 - e, translateYPx: -e * 40 * k };
        case 'slideDown':
            return { ...IDENTITY, opacity: 1 - e, translateYPx: e * 40 * k };
        case 'blurIn':
            return { ...IDENTITY, opacity: 1 - e, blurPx: e * 10 * k };
        default:
            return { ...IDENTITY, opacity: 1 - e };
    }
}
/**
 * Active-word motion. For the one-shot kinds it's a brief scale-up settling to a
 * resting size. For the looping kinds (wiggle / jelly / glow) it uses the
 * absolute clock `nowMs` so the motion cycles for as long as the word is spoken;
 * `spec.durationMs` is the loop period and `spec.intensity` the amplitude.
 */
export function activeWordTransform(spec, activeProgress, targetScale, nowMs = 0) {
    if (spec.kind === 'wiggle' || spec.kind === 'jelly' || spec.kind === 'glow') {
        const period = spec.durationMs > 0 ? spec.durationMs : 600;
        const phase = ((nowMs % period) / period) * Math.PI * 2;
        const k = spec.intensity <= 0 ? 1 : spec.intensity;
        if (spec.kind === 'wiggle') {
            return { ...IDENTITY, scale: targetScale, rotateDeg: Math.sin(phase) * 5 * k };
        }
        if (spec.kind === 'jelly') {
            // Squash on X, stretch on Y (and back), around the resting size.
            const s = Math.sin(phase) * 0.12 * k;
            return { ...IDENTITY, scale: targetScale * (1 + s), scaleYMul: (1 - s) / (1 + s) };
        }
        // glow: a soft breathing pulse (a true blur-glow isn't a per-word transform).
        const pulse = 0.5 + 0.5 * Math.sin(phase);
        return { ...IDENTITY, scale: targetScale * (1 + 0.05 * k * pulse) };
    }
    if (spec.kind === 'none')
        return { ...IDENTITY, scale: targetScale };
    // Ramp to targetScale quickly, then settle back toward a resting emphasized
    // scale (halfway between 1 and target) so the word stays slightly enlarged.
    const rest = 1 + (targetScale - 1) * 0.5;
    const peakAt = 0.28;
    let scale;
    if (activeProgress <= peakAt) {
        const t = ease(activeProgress / peakAt, 'easeOut');
        scale = 1 + (targetScale - 1) * t;
    }
    else {
        const t = ease((activeProgress - peakAt) / (1 - peakAt), 'easeInOut');
        scale = targetScale + (rest - targetScale) * t;
    }
    return { ...IDENTITY, scale };
}
function popScale(t, intensity) {
    // Grows from 0.7 to 1.0 with a brief overshoot governed by intensity.
    const overshoot = 0.18 * intensity;
    const e = ease(t, 'easeOut');
    const wobble = Math.sin(t * Math.PI) * overshoot * (1 - t);
    return 0.7 + 0.3 * e + wobble;
}
function bounce(t) {
    return Math.sin(t * Math.PI * 3) * (1 - t);
}
export function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
/** Compose two transforms (entrance ∘ active), multiplying scale/opacity. */
export function composeTransforms(a, b) {
    return {
        opacity: a.opacity * b.opacity,
        scale: a.scale * b.scale,
        translateXPx: a.translateXPx + b.translateXPx,
        translateYPx: a.translateYPx + b.translateYPx,
        blurPx: Math.max(a.blurPx, b.blurPx),
        letterSpacingDeltaPx: a.letterSpacingDeltaPx + b.letterSpacingDeltaPx,
        rotateDeg: a.rotateDeg + b.rotateDeg,
        scaleYMul: a.scaleYMul * b.scaleYMul,
    };
}
//# sourceMappingURL=animation.js.map